import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl, intlShape } from 'react-intl';
import Head from 'next/head';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import {
  setSearchParams as setSearchParamsAction,
  fetchAdsVisitCounter,
  enableRetrackAdImpressions,
  setSearchParamsPage,
} from './actions';
import { setQueryTrovit as setQueryTrovitAction, saveLastSearch } from '../Index/actions';
import { getAlertSection } from '../Alerts/email/selectors';
import * as TrackEvents from '../tracking/events';
import { trackEvent, locationChange, reTrackImpressions } from '../tracking/actions';
import {
  titleSelector,
  descriptionSelector,
  countSelector,
  similarContentAdsCountSelector,
  errorSelector,
  didYouMeanWhatSelector,
  breadcrumbSelector,
  similarSelector,
  propertyTypeSelector,
  geoSelector,
  geoHorizontalSelector,
  makeSelector,
  modelSelector,
  getOtherJobCategories,
  getOldLocation,
  getSerpTitle,
  getTypeName,
  selectedSeoSelector,
  lastFreshAdSelector,
  adWithMinPriceSelector,
  locationSelector,
  isFetchingSelector,
  searchParametersSelector,
  shouldRetrackAdImpressionsSelector,
  showingFiltersSelector,
  getFilters,
} from './selectors';
import { queryAlertLandingSelector, queryOriginSelector } from '../request/query/selectors';
import { previousUrlSelector } from '../request/url/selectors';
import { nextPageIndex, previousPageIndex, addPageNumber } from '../utils/page';
import Layout from '../common/Layout';
import { Error } from '../Error/components';
import HeaderSerp from '../common/Header/HeaderSerp/container';
import NotificationsManagerCtaLink from '../NotificationsManager/email/CallToActionLink/container';
import { SnackError } from '../components/mobile/SnackError';
import Loader from '../common/Loader';
import Breadcrumb from '../common/Breadcrumb/container';
import { InterlinkingJobs, InterlinkingCars, InterlinkingHomes } from './components/Interlinking';
import { List } from './components/Lists';
import Paginate from './components/Paginate';
import PublishYourCv from './components/PublishYourCv/container';
import ListHead from './components/ListHead/ListHeadContainer';
import ExpiredAd from './components/ExpiredAd/container';
import DownloadAppTopBanner from '../DownloadAppBanner/TopBanner/container';
import Filters from './components/Filters/shared/container';
import messages from './messages';
import { Router } from '../router';
import SectionIds from '../tracking/SectionIds';
import { ORIGIN_BROWSER_NOTIFICATION } from '../request/origin/constants';
import { RelatedAdsModal } from './components/Modals';
import {
  tranformQuerySeoToSearchParametersObject,
  tranformQueryToSearchParametersObject,
  computeParamsForPage,
} from '../utils/searchParameters';
import RedirectingToLayout from '../Sources/redirectingTo/components/RedirectingToLayout/container';
import RedirectingToSwitch from '../Sources/redirectingTo/components/RedirectingToSwitch/container';
import SourceLoader from '../Sources/loader/components/SourceLoader/container';
import SourceLoaderSwitch from '../Sources/loader/components/SourceLoaderSwitch/container';
import { showSourcesLoader, updateSourcesLoader } from '../Sources/loader/actions';
import { updateWindowScrollPosition } from '../UI/scroll/actions';
import Title from '../components/shared/Titles/MetaTitle';
import { countAppliedFilters } from './utils';
import { transformOldTrovitUrlPathToQuery } from '../utils/oldTrovit/url';
import { isQueryAlertLanding } from '../request/query/utils';
import { VERTICAL_NAMES } from '../common/constants/common';
import { DEFAULT_IMAGE_META } from '../constants';
import { EventTracking } from '../tracking/components';
import internalstatsAlertImpressionTracking from '../tracking/events/internalstatsAlertImpression/apiTracking';
import TrackingApi from '../sdk/tracking/Api';
import clientConfig from '../../parameters/client/config';
import { getOpenPushNotificationEventData } from '../tracking/eventDefinitions';
import { TEST_IDS } from '../TestAb/constants';
import { TestAbSwitch } from '../TestAb/components';
import { DisambiguationList, DisambiguationTagsList } from '../common/Lists';
import ImprovedAlert from './components/ImprovedAlert/container';
import { getPreviousPageUrl, getNextPageUrl } from './components/shared/HeadMeta/selectors';
import setSearchFiltersVisibility from './components/Filters/shared/utils/setSearchFiltersVisibility';
import { showSearchFilters, hideSearchFilters } from './components/Filters/shared/actions';
import {
  isPersonalizedAdsEnabled,
  isGdprCookieBannerVisible as isGdprCookieBannerVisibleSelector,
} from '../Gdpr/selectors';
import GdprCookieBanner from '../Gdpr/components/mobile/GdprCookieBanner/container';
import { verticalNameSelector, configSelector } from '~/common/selectors/config';
import { trovitSelector } from '~/common/selectors/trovit';

const trackingApi = new TrackingApi(clientConfig.trackingApi);
const { descriptionPriceFrom, descriptionNoPrice } = messages;

class Serp extends React.Component {
  static showNextPageLink(totalCount, currentPage, organicResults) {
    return totalCount - organicResults * currentPage > 0;
  }

  static renderMetaRobots(isSeoUrl, isSemUrl, page) {
    return isSeoUrl && !isSemUrl && page <= 10 ? (
      <meta name="robots" content="index,follow" />
    ) : (
      <meta name="robots" content="noindex,nofollow" />
    );
  }

  static getOriginToAds(thirdPartyAds, origin) {
    if (!thirdPartyAds) {
      return 0;
    }
    const thirdPartyAdsOrigins = Object.keys(thirdPartyAds);
    return thirdPartyAdsOrigins.indexOf(`${origin}`) > -1 ? origin : 0;
  }

  constructor(props) {
    super(props);

    this.handleNavigatorHistoryButtons = this.handleNavigatorHistoryButtons.bind(this);
    this.retryRequest = this.retryRequest.bind(this);
    this.trackScrolled = this.trackScrolled.bind(this);
    this.showFilters = this.showFilters.bind(this);
    this.hideFilters = this.hideFilters.bind(this);
  }

  componentDidMount() {
    Router.prefetch('/index');

    window.addEventListener('beforeunload', this.trackScrolled);
    window.addEventListener('popstate', this.handleNavigatorHistoryButtons);

    if (this.props.shouldRetrackAdImpressions) {
      this.props.reTrackImpressions();
    }

    this.props.saveLastSearch(location.href);
    this.props.fetchAdCounterClicks();
    this.props.updateWindowScrollPosition();

    this.trackOpenPushNotification();
  }

  componentWillReceiveProps(nextProps) {
    // NOTE: This method is mainly used to update the component when the user
    // uses the browser back/forward buttons.
    // The idea is to extrack information from the url (obtained via getInitialProps
    // and stores in the 'query' property) and determine the right searchParameters.
    const nextQuery = nextProps.query;
    const currentQuery = this.props.query;
    const currentSearchParameters = this.props.searchParameters;

    // Ensure both next and current queries have the page property.
    if (!nextQuery.page) {
      nextQuery.page = 1;
    }

    if (!currentQuery.page) {
      currentQuery.page = 1;
    }

    // Force show sources loader whan changing search by SeoWhere Interlinking
    if (typeof nextQuery.seoWhere !== 'undefined' && nextQuery.seoWhere !== currentQuery.seoWhere) {
      this.props.showSourcesLoader();
    }

    // Force show sources loader whan changing search by Seo Interlinking
    if (typeof nextQuery.seo !== 'undefined' && nextQuery.seo !== currentQuery.seo) {
      this.props.showSourcesLoader();
    }

    if (typeof nextQuery.seo !== 'undefined') {
      const hasChanged = !isEqual(nextQuery, currentQuery);
      if (hasChanged && !nextProps.isFetching) {
        const searchParams = tranformQuerySeoToSearchParametersObject(
          nextQuery,
          currentSearchParameters
        );
        const hasTermChanged = nextQuery.seo !== currentQuery.seo;

        this.props.setSearchParams(searchParams, hasTermChanged);
      }
    }

    // Track pageview when are changes on what_d, page, type or SEO keyword
    if (
      nextQuery.what_d !== currentQuery.what_d ||
      nextQuery.page !== currentQuery.page ||
      nextQuery.type !== currentQuery.type ||
      nextQuery.seo !== currentQuery.seo
    ) {
      const { url, query, statusCode } = nextProps;

      this.props.locationChange({ url, query, statusCode });
    }

    if (nextProps.url !== this.props.url) {
      this.props.saveLastSearch(location.href);
    }
  }

  shouldComponentUpdate(nextProps) {
    if (
      get(nextProps.searchParameters, 'pageName') !== '' &&
      get(nextProps.searchParameters, 'pageName') !== 'serp'
    ) {
      return false;
    }
    return true;
  }

  componentWillUnmount() {
    window.removeEventListener('beforeunload', this.trackScrolled);
    window.removeEventListener('popstate', this.handleNavigatorHistoryButtons);

    this.trackScrolled();

    // Reset source loader
    const lastSearchUrl = this.props.config.lastSearch;
    this.props.updateSourcesLoader({ lastSearchUrl });

    // Set reTrack impressions flag to true so when comming from a page
    // that does not trigger a search (like a back button) we retrigger the impression tracking
    // this is due to track impressions on FETCH_ADS_SUCCESS action.
    this.props.enableRetrackAdImpressions();
  }

  // We send the OPEN push notification tracking event when we are sure that
  // the user has arrived to the retention serp (not in service worker)
  trackOpenPushNotification() {
    const { lastFreshAd, trovit, config } = this.props;
    const origin = trovit.origin;
    const isPushOpen = config.isPushOpen;

    if (!isPushOpen && lastFreshAd && origin === ORIGIN_BROWSER_NOTIFICATION) {
      const data = getOpenPushNotificationEventData(config, trovit);
      trackingApi.notificationBrowserEvents(data);
    }
  }

  handleNavigatorHistoryButtons() {
    if (
      get(this.props, 'query.adwordsParams') &&
      typeof this.props.query.adwordsParams === 'string'
    ) {
      const url = new URL(this.props.url).pathname;
      const urlParsed = transformOldTrovitUrlPathToQuery(url);
      const query = { ...this.props.query, ...urlParsed };
      const searchParams = tranformQueryToSearchParametersObject(
        query,
        this.props.searchParameters
      );

      const hasTermChanged = query.what_d !== get(this.props.searchParameters, 'what_d');
      if (hasTermChanged) {
        this.props.setQueryTrovit({ query });
        this.props.setSearchParams(searchParams, hasTermChanged);
      }
    } else if (this.props.previousUrl !== this.props.url) {
      const query = this.props.query;
      if (typeof query.seo === 'undefined' && !query.isFetching) {
        const searchParams = tranformQueryToSearchParametersObject(
          query,
          this.props.searchParameters
        );

        this.props.setSearchParams(searchParams, true);
      }
    }
    this.hideFilters();
  }

  retryRequest() {
    this.props.setSearchParams(this.props.searchParameters, false);
  }

  trackScrolled() {
    const isScrolled = window.pageYOffset >= window.innerHeight;

    if (isScrolled) {
      this.props.trackEvent(TrackEvents.scrolledInteractionEvent());
    } else {
      this.props.trackEvent(TrackEvents.noscrolledInteractionEvent());
    }
  }

  showFilters() {
    const showFilters = true;

    this.toggleFilters(showFilters);
  }

  hideFilters() {
    const hideFilters = false;

    this.toggleFilters(hideFilters);
  }

  toggleFilters(visibility) {
    setSearchFiltersVisibility({
      visibility,
      showSearchFilters: this.props.showSearchFilters,
      hideSearchFilters: this.props.hideSearchFilters,
      trackEvent: this.props.trackEvent,
      TrackEvents,
    });
  }

  computeDescription() {
    const { description, adWithMinPrice, intl, searchParameters } = this.props;
    const PLACEHOLDER_MIN_PRICE_DESCRIPTION = '{{min_price_description}}';
    const page = get(searchParameters, 'page');

    let text = description || '';

    if (text.indexOf(PLACEHOLDER_MIN_PRICE_DESCRIPTION) !== -1) {
      let priceText;

      if (adWithMinPrice) {
        const price = get(adWithMinPrice, ['attributes', 'vertical_properties', 'price', 'value']);
        priceText = intl.formatMessage(descriptionPriceFrom, { '%1$s': intl.formatNumber(price) });
      } else {
        priceText = intl.formatMessage(descriptionNoPrice);
      }

      text = text.replace(PLACEHOLDER_MIN_PRICE_DESCRIPTION, priceText);
    }

    return addPageNumber(text, page, intl);
  }

  /**
   * @todo TestAB PROD-382
   * correctly implement or remove this line when rollout
   */
  isImprovedAlert() {
    const { query, config, searchParameters } = this.props;
    const isHomesVertical = config.verticalName === VERTICAL_NAMES.homes;
    const isRetention = get(searchParameters, 'boost_ads_since');

    return (
      isHomesVertical && isRetention && query.internalsection && query.internalsection === '1060'
    );
  }

  render() {
    const {
      title,
      searchParameters,
      isFetching,
      interlinking,
      location,
      count,
      similarContentAdsCount,
      lastFreshAd,
      error,
      didYouMeanWhat,
      config,
      trovit,
      queryAlertLanding,
      showingFilters,
      personalizedAds,
      filters,
    } = this.props;

    const hasResults = count > 0;
    if (!hasResults && !isFetching && !showingFilters) {
      if (!error || (error && (error.code === 404 || error.status === 404))) {
        return <Error statusCode={404} />;
      }
    }

    const verticalName = config.verticalName;
    const countryName = get(config, ['vertical', 'countryName']);
    const geoNames = location
      ? {
          city: get(location, 'city'),
          cityArea: get(location, 'city_area'),
          region: get(location, 'region'),
        }
      : { country: countryName };

    const descriptionText = this.computeDescription();

    const appliedFiltersObject = filters;
    const appliedFiltersCount = countAppliedFilters(appliedFiltersObject);

    const page = get(searchParameters, 'page');
    const isSeoUrl = get(searchParameters, 'isSeoUrl');
    const isSemUrl = get(searchParameters, 'isSemUrl');
    const isExpiredAd = this.props.query.expired;

    const isImprovedAlert = this.isImprovedAlert();

    const showNext = Serp.showNextPageLink(
      count,
      page,
      get(config, ['vertical', 'adsPerSections', 'organic'])
    );

    const thirdPartyAds = get(config, ['vertical', 'thirdPartyAds']);
    const originAdsDefault = 0;
    const originToAds = Serp.getOriginToAds(
      thirdPartyAds,
      isSemUrl ? trovit.origin : originAdsDefault
    );
    const thirdPartyAdsConfig = thirdPartyAds ? get(thirdPartyAds, [`${originToAds}`]) : null;
    const isPushDisallow = config.isPushDisallow;
    const alertLanding = isQueryAlertLanding(Number(queryAlertLanding));

    /**
     * Title generation logic moved to this class because next/head component doesn't works well with React Components + Intl
     * More info: https://github.com/zeit/next.js/issues/1636
     */
    const { intl, categoryName, serpTitle, previousPageUrl, nextPageUrl } = this.props;
    const metaTitle = new Title(
      intl,
      verticalName,
      categoryName,
      serpTitle,
      searchParameters
    ).getTitle(true);

    return (
      <RedirectingToSwitch>
        {isRedirectingToShow => {
          if (isRedirectingToShow) {
            return <RedirectingToLayout />;
          }

          return (
            <Layout>
              <Head>
                <title>{metaTitle}</title>
                <meta name="description" content={descriptionText} />
                <meta property="og:title" content={metaTitle} />
                <meta property="og:description" content={descriptionText} />
                <meta property="og:image" content={DEFAULT_IMAGE_META} />
                {Serp.renderMetaRobots(isSeoUrl, isSemUrl, page)}
                {previousPageUrl && <link rel="prev" href={previousPageUrl} />}
                {nextPageUrl && <link rel="next" href={nextPageUrl} />}
              </Head>

              {config.showDownloadAppBanner &&
              !alertLanding &&
              isPushDisallow &&
              verticalName !== 'products' &&
              !this.props.isGdprCookieBannerVisible ? (
                <DownloadAppTopBanner currentUrl={this.props.url} />
              ) : null}

              <HeaderSerp currentSearch={get(searchParameters, 'whatAccented')} />

              <GdprCookieBanner />

              {lastFreshAd > 0 ? <NotificationsManagerCtaLink /> : null}

              <SourceLoaderSwitch>
                {isSourceLoaderShow =>
                  isSourceLoaderShow ? (
                    <SourceLoader />
                  ) : (
                    <React.Fragment>
                      <ListHead
                        searchParameters={searchParameters}
                        count={count}
                        showFilters={this.showFilters}
                        appliedFiltersCount={appliedFiltersCount}
                        wrongWhat={didYouMeanWhat}
                      />
                      <TestAbSwitch id={TEST_IDS.disambiguationList} option="b">
                        {isTestOption =>
                          isTestOption ? (
                            <React.Fragment>
                              <DisambiguationList searchParameters={searchParameters} />
                              <DisambiguationTagsList />
                            </React.Fragment>
                          ) : null
                        }
                      </TestAbSwitch>
                    </React.Fragment>
                  )
                }
              </SourceLoaderSwitch>

              {isExpiredAd && <ExpiredAd />}

              {/* TestAB PROD-382 */}
              {isImprovedAlert ? <ImprovedAlert /> : null}

              <List
                adsCount={count}
                similarContentAdsCount={similarContentAdsCount}
                searchParameters={searchParameters}
                isFetching={isFetching}
                trovit={trovit}
                config={config}
                thirdPartyAdsConfig={thirdPartyAdsConfig}
                lastFreshAd={lastFreshAd}
                personalizedAds={personalizedAds}
              />

              <Filters hideFilters={this.hideFilters} animated={!hasResults} />

              <Paginate
                currentPage={get(searchParameters, 'page')}
                showNext={showNext}
                prevPageParams={computeParamsForPage(
                  previousPageIndex(get(searchParameters, 'page')),
                  searchParameters,
                  this.props.verticalName,
                  this.props.queryOrigin,
                  this.props.alertLanding,
                  get(this.props, 'query.tg')
                )}
                nextPageParams={computeParamsForPage(
                  nextPageIndex(get(searchParameters, 'page')),
                  searchParameters,
                  this.props.verticalName,
                  this.props.queryOrigin,
                  this.props.alertLanding,
                  get(this.props, 'query.tg')
                )}
                query={this.props.query}
                onPageChange={this.props.changePage}
              />

              {isSemUrl ? null : <Breadcrumb />}

              {verticalName !== VERTICAL_NAMES.products && isPushDisallow ? (
                <EventTracking
                  event={internalstatsAlertImpressionTracking}
                  sectionId={
                    isSemUrl
                      ? SectionIds.ALERT_CREATION_SECTION_ADWORDS_MOBILE_BOTTOM
                      : SectionIds.ALERT_CREATION_SECTION_MOBILE_BOTTOM
                  }
                  isForm={false}
                />
              ) : null}

              {verticalName === VERTICAL_NAMES.jobs ? <PublishYourCv /> : null}

              {!isSemUrl && isSeoUrl && verticalName === VERTICAL_NAMES.homes ? (
                <InterlinkingHomes links={interlinking} geoNames={geoNames} />
              ) : null}

              {!isSemUrl && isSeoUrl && verticalName === VERTICAL_NAMES.cars ? (
                <InterlinkingCars links={interlinking} geoNames={geoNames} />
              ) : null}

              {!isSemUrl && isSeoUrl && verticalName === VERTICAL_NAMES.jobs ? (
                <InterlinkingJobs
                  links={interlinking}
                  location={location}
                  countryName={countryName}
                />
              ) : null}

              {error && !showingFilters && error.code !== 404 ? (
                <SnackError error={error} buttonAction={this.retryRequest} />
              ) : null}

              <Loader isFetching={isFetching} />
              <RelatedAdsModal />
            </Layout>
          );
        }}
      </RedirectingToSwitch>
    );
  }
}

Serp.defaultProps = {
  error: null,
  query: null,
  queryOrigin: null,
  url: null,
  previousUrl: null,
  location: null,
  title: null,
  description: null,
  adWithMinPrice: null,
  statusCode: null,
  alertsError: null,
  didYouMeanWhat: null,
  categoryName: null,
  serpTitle: null,
  queryAlertLanding: null,
  previousPageUrl: null,
  nextPageUrl: null,
};

Serp.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  searchParameters: PropTypes.object.isRequired,
  location: PropTypes.object,
  setSearchParams: PropTypes.func.isRequired,
  setQueryTrovit: PropTypes.func.isRequired,
  trackEvent: PropTypes.func.isRequired,
  locationChange: PropTypes.func.isRequired,
  count: PropTypes.number.isRequired,
  similarContentAdsCount: PropTypes.number.isRequired,
  lastFreshAd: PropTypes.number.isRequired,
  statusCode: PropTypes.number,
  interlinking: PropTypes.object.isRequired,
  previousUrl: PropTypes.string,
  url: PropTypes.string,
  query: PropTypes.object,
  adWithMinPrice: PropTypes.object,
  error: PropTypes.object,
  didYouMeanWhat: PropTypes.string,
  isFetching: PropTypes.bool.isRequired,
  intl: intlShape, // eslint-disable-line react/require-default-props
  config: PropTypes.object.isRequired,
  trovit: PropTypes.object.isRequired,
  reTrackImpressions: PropTypes.func.isRequired,
  fetchAdCounterClicks: PropTypes.func.isRequired,
  shouldRetrackAdImpressions: PropTypes.bool.isRequired,
  enableRetrackAdImpressions: PropTypes.func.isRequired,
  showSourcesLoader: PropTypes.func.isRequired,
  updateSourcesLoader: PropTypes.func.isRequired,
  isGdprCookieBannerVisible: PropTypes.bool.isRequired,
  categoryName: PropTypes.string,
  serpTitle: PropTypes.string,
  saveLastSearch: PropTypes.func.isRequired,
  changePage: PropTypes.func.isRequired,
  queryAlertLanding: PropTypes.number,
  showingFilters: PropTypes.bool.isRequired,
  showSearchFilters: PropTypes.func.isRequired,
  hideSearchFilters: PropTypes.func.isRequired,
  verticalName: PropTypes.string.isRequired,
  queryOrigin: PropTypes.string,
  alertLanding: PropTypes.number.isRequired,
  updateWindowScrollPosition: PropTypes.func.isRequired,
  previousPageUrl: PropTypes.string,
  nextPageUrl: PropTypes.string,
  personalizedAds: PropTypes.bool.isRequired,
  filters: PropTypes.object,
};

export const mapStateToProps = (state, props) => ({
  title: titleSelector(state),
  description: descriptionSelector(state),
  adWithMinPrice: adWithMinPriceSelector(state),
  location: locationSelector(state),
  searchParameters: searchParametersSelector(state),
  count: countSelector(state),
  similarContentAdsCount: similarContentAdsCountSelector(state),
  lastFreshAd: lastFreshAdSelector(state),
  error: errorSelector(state),
  didYouMeanWhat: didYouMeanWhatSelector(state),
  previousUrl: previousUrlSelector(state),
  interlinking: {
    breadcrumb: breadcrumbSelector(state),
    similar: similarSelector(state),
    propertyType: propertyTypeSelector(state),
    geo: geoSelector(state),
    geoHorizontal: geoHorizontalSelector(state),
    make: makeSelector(state),
    model: modelSelector(state),
    otherJobCategories: getOtherJobCategories(state),
    oldLocation: getOldLocation(state),
    selectedSeoLinks: selectedSeoSelector(state),
  },
  isFetching: isFetchingSelector(state),
  config: configSelector(state),
  trovit: trovitSelector(state),
  shouldRetrackAdImpressions: shouldRetrackAdImpressionsSelector(state),
  isGdprCookieBannerVisible: isGdprCookieBannerVisibleSelector(state),
  serpTitle: getSerpTitle(state),
  categoryName: getTypeName(state, props.intl),
  queryAlertLanding: queryAlertLandingSelector(state),
  showingFilters: showingFiltersSelector(state),
  alertSection: getAlertSection(state),
  verticalName: verticalNameSelector(state),
  queryOrigin: queryOriginSelector(state),
  alertLanding: queryAlertLandingSelector(state),
  previousPageUrl: getPreviousPageUrl(state),
  nextPageUrl: getNextPageUrl(state),
  personalizedAds: isPersonalizedAdsEnabled(state),
  filters: getFilters(state),
});

export const mapDispatchToProps = dispatch => ({
  setSearchParams: (searchParams, hasTermChanged) =>
    dispatch(setSearchParamsAction(searchParams, hasTermChanged)),
  trackEvent: event => dispatch(trackEvent(event)),
  locationChange: data => dispatch(locationChange(data)),
  reTrackImpressions: () => dispatch(reTrackImpressions()),
  enableRetrackAdImpressions: () => dispatch(enableRetrackAdImpressions()),
  fetchAdCounterClicks: () => dispatch(fetchAdsVisitCounter()),
  showSourcesLoader: () => dispatch(showSourcesLoader()),
  updateSourcesLoader: config => dispatch(updateSourcesLoader(config)),
  setQueryTrovit: query => dispatch(setQueryTrovitAction(query)),
  saveLastSearch: url => dispatch(saveLastSearch(url)),
  changePage: newPage => dispatch(setSearchParamsPage(newPage)),
  showSearchFilters: () => dispatch(showSearchFilters()),
  hideSearchFilters: () => dispatch(hideSearchFilters()),
  updateWindowScrollPosition: () => dispatch(updateWindowScrollPosition()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(injectIntl(Serp));

export const PureSerp = Serp;



// WEBPACK FOOTER //
// ./app/Serp/index.js